import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ckeditorFrameDocument } from './ckeditorFrame';
import { preserveDocument } from './htmlDocument';
import './HtmlEditor.css';

export default function HtmlEditor({ value = '', onChange, name = 'html', label = 'HTML' }) {
  const [mode, setMode] = useState('visual');
  const [error, setError] = useState(false);
  const [height, setHeight] = useState(500);
  const frame = useRef(null);
  const ready = useRef(false);
  const currentValue = useRef(value);
  const callback = useRef(onChange);
  const channel = useRef(`ckeditor-${Math.random().toString(36).slice(2)}`);
  callback.current = onChange;
  const document = useMemo(() => ckeditorFrameDocument(new URL(
    `${process.env.PUBLIC_URL || ''}/vendor/ckeditor/4.11.1/`, window.location.origin
  ).href, channel.current, window.location.origin), []);

  useEffect(() => {
    ready.current = false;
    if (mode !== 'visual') return undefined;
    function receive(event) {
      if (event.source !== frame.current?.contentWindow || event.data?.channel !== channel.current) return;
      if (event.data.type === 'ready') {
        ready.current = true;
        frame.current.contentWindow.postMessage({channel:channel.current,type:'load',html:currentValue.current}, '*');
      } else if (event.data.type === 'change' && typeof event.data.html === 'string') {
        const parsed = new DOMParser().parseFromString(event.data.html, 'text/html');
        const html = event.data.mode === 'source' ? event.data.html
          : preserveDocument(currentValue.current, parsed.body.innerHTML);
        if (html !== currentValue.current) {
          currentValue.current = html;
          callback.current?.(html);
        }
      } else if (event.data.type === 'resize' && Number.isFinite(event.data.height)) {
        setHeight(Math.max(420, Math.min(1000, event.data.height)));
      } else if (event.data.type === 'error') {
        setError(true); setMode('html');
      }
    }
    window.addEventListener('message', receive);
    return () => { ready.current = false; window.removeEventListener('message', receive); };
  }, [mode]);

  useEffect(() => {
    if (value === currentValue.current) return;
    currentValue.current = value;
    if (ready.current && frame.current) {
      frame.current.contentWindow.postMessage({channel:channel.current,type:'load',html:value}, '*');
    }
  }, [value]);

  function changeSource(event) {
    currentValue.current = event.target.value;
    callback.current?.(event.target.value);
  }

  return <div className="html-editor">
    <div className="btn-group mb-2" role="group" aria-label={`${label} editor`}>
      <button type="button" className={`btn btn-sm ${mode === 'visual' ? 'btn-primary' : 'btn-outline-primary'}`}
        aria-pressed={mode === 'visual'} onClick={() => setMode('visual')}>Visual</button>
      <button type="button" className={`btn btn-sm ${mode === 'html' ? 'btn-primary' : 'btn-outline-primary'}`}
        aria-pressed={mode === 'html'} onClick={() => setMode('html')}>HTML</button>
    </div>
    {error && <p className="text-danger" role="alert">Unable to load the visual editor. HTML editing is available.</p>}
    {mode === 'visual' ? <iframe ref={frame} title={`${label} visual editor`} data-channel={channel.current}
      className="html-editor__frame" style={{height}} sandbox="allow-scripts" srcDoc={document} />
      : <textarea className="form-control html-editor__source" name={name} aria-label={`${label} source`}
        value={value} onChange={changeSource} spellCheck={false} rows={18} />}
  </div>;
}
